// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ViewLoAModal, ToggleSwitch, Button } from '@loa/ui-components';
import { signedDocumentApi } from '../api/signedDocumentApi';
import { clientDocumentApi } from '../api/clientDocumentApi';
import type {
  SignedDocumentDTO,
  SignedDocumentStatus,
  SignedDocumentType,
} from '../types/signedDocument';
import type { PageResponse } from '../types/relationship';
import { LOATableCard, EyeIcon, DownloadIcon, TrashIcon } from './loa';
import type { LOATableColumn, LOATableAction } from './loa';
import { DocumentTypeBadge } from './signedDocuments/DocumentTypeBadge';
import { UploadRelationshipDocumentModal } from './documents/UploadRelationshipDocumentModal';
import { DeleteDocumentModal } from './documents/DeleteDocumentModal';
import './LOAList.css';

export type ClientSignedDocumentsListProps = Readonly<{
  profileId: string;
  /** When provided, fetches per-policy slices instead of profile-level documents. */
  policyId?: string;
  readOnly?: boolean;
  aboveTable?: React.ReactNode;
}>;

/**
 * Unified "Client Signed Documents" list that merges LOAs and CoAs into a
 * single chronological stream. Replaces the legacy LOA-only
 * {@code LOAList} on the relationship detail page. View and download
 * actions branch on {@code documentType} so each row hits the correct
 * underlying API ({@link loaApi} or {@link coaApi}).
 */
export function ClientSignedDocumentsList({
  profileId,
  policyId,
  readOnly = false,
  aboveTable,
}: ClientSignedDocumentsListProps) {
  const [page, setPage] = useState<PageResponse<SignedDocumentDTO>>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 10,
    number: 0,
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [viewPdfState, setViewPdfState] = useState<
    | { row: SignedDocumentDTO; title: string }
    | null
  >(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [viewContentType, setViewContentType] = useState<string | undefined>(undefined);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingRow, setDeletingRow] = useState<SignedDocumentDTO | null>(null);

  // Upload + the "Show to client" toggle are relationship-level features only.
  // On the policy detail tab (policyId set) the table stays read-only.
  const relationshipMode = !policyId && !readOnly;

  const loadPage = useCallback(
    async (zeroBasedPage: number = 0) => {
      try {
        setLoading(true);
        setError(null);
        const data = policyId
          ? await signedDocumentApi.getSignedDocumentsByPolicy(policyId, zeroBasedPage, pageSize)
          : await signedDocumentApi.getSignedDocumentsByProfile(profileId, zeroBasedPage, pageSize);
        setPage(data);
      } catch (err) {
        const e = err as { response?: { data?: { message?: string } } };
        setError(e.response?.data?.message || 'Failed to load signed documents');
      } finally {
        setLoading(false);
      }
    },
    [profileId, policyId, pageSize]
  );

  useEffect(() => {
    loadPage(currentPage - 1);
  }, [profileId, policyId, currentPage, loadPage]);

  const downloadForRow = useCallback(
    (row: SignedDocumentDTO): Promise<Blob> => {
      // Client uploads download by id so the real content type is preserved
      // (uploads may be image/Word/Excel; the generic signed-document download
      // forces application/pdf and would corrupt non-PDF files).
      if (row.documentType === 'UPLOAD') {
        return clientDocumentApi.download(row.id).then((r) => r.blob);
      }
      if (row.signedDocumentUrl) {
        return signedDocumentApi.download(row.signedDocumentUrl);
      }
      return Promise.reject(new Error('No signed document URL available'));
    },
    []
  );

  const handleDownload = useCallback(
    async (row: SignedDocumentDTO) => {
      try {
        setDownloadingId(row.id);
        const blob = await downloadForRow(row);
        const url = globalThis.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${row.documentType}-${row.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
      } catch (err) {
        const e = err as { response?: { data?: { message?: string } } };
        setError(e.response?.data?.message || `Failed to download ${row.documentType}`);
      } finally {
        setDownloadingId(null);
      }
    },
    [downloadForRow]
  );

  const handleViewPdf = useCallback(
    async (row: SignedDocumentDTO) => {
      let title: string;
      if (row.documentType === 'LOA' || row.documentType === 'COA') {
        title = `${row.documentType === 'LOA' ? 'LOA' : 'CoA'} · ${getStatusLabel(row.status)}`;
      } else {
        // UPLOAD / ASSESSMENT / SOS / NEW_BUSINESS: the row's own name is the title.
        title = row.documentName || 'Document';
      }
      setViewPdfState({ row, title });
      setPdfLoading(true);
      setPdfUrl(null);
      setViewContentType(undefined);
      try {
        const blob = await downloadForRow(row);
        const url = URL.createObjectURL(blob);
        setViewContentType(blob.type || undefined);
        setPdfUrl(url);
      } catch {
        // Keep pdfUrl null — modal renders "Document not available"
      } finally {
        setPdfLoading(false);
      }
    },
    [downloadForRow]
  );

  const handleClosePdfViewer = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setViewContentType(undefined);
    setViewPdfState(null);
  };

  const handleToggleVisibility = useCallback(
    async (row: SignedDocumentDTO, next: boolean) => {
      // Optimistic flip; revert on failure.
      setTogglingId(row.id);
      setPage((prev) => ({
        ...prev,
        content: prev.content.map((r) =>
          r.id === row.id ? { ...r, visibleToClient: next } : r,
        ),
      }));
      try {
        await signedDocumentApi.setVisibility(row.documentType, row.id, next);
      } catch (err) {
        setPage((prev) => ({
          ...prev,
          content: prev.content.map((r) =>
            r.id === row.id ? { ...r, visibleToClient: !next } : r,
          ),
        }));
        const e = err as { response?: { data?: { message?: string } } };
        setError(e.response?.data?.message || 'Failed to update visibility');
      } finally {
        setTogglingId(null);
      }
    },
    [],
  );

  const handleUpload = useCallback(
    async (documentName: string, documentType: string, file: File) => {
      await clientDocumentApi.uploadToRelationship(profileId, file, documentName, documentType);
      await loadPage(currentPage - 1);
    },
    [profileId, currentPage, loadPage],
  );

  const columns = useMemo<LOATableColumn<SignedDocumentDTO>[]>(
    () => [
      {
        key: 'documentName',
        label: 'Document name',
        render: (row) => (
          <span className="loa-doc-name" title={row.documentName ?? undefined}>
            {row.documentName || '—'}
          </span>
        ),
      },
      {
        key: 'signatory',
        label: 'Signatory',
        render: (row) => (
          <span className="loa-signatory-name">{row.signatoryName || 'NA'}</span>
        ),
      },
      {
        key: 'type',
        label: 'Document type',
        render: (row) => <DocumentTypeBadge documentType={row.documentType} />,
      },
      {
        key: 'status',
        label: 'Status',
        render: (row) =>
          row.documentType === 'UPLOAD' || !row.status ? (
            <span className="loa-count">NA</span>
          ) : (
            <span className={getStatusBadgeClass(row.status)}>
              {getStatusLabel(row.status)}
            </span>
          ),
      },
      // "Show to client" — relationship tab only. Rendered for signed LoA/CoA
      // and advisor uploads (backend populates `visibleToClient` for those);
      // client uploads carry null and render a dash (clients always see them).
      ...(relationshipMode
        ? [
            {
              key: 'showToClient',
              label: 'Show to client',
              render: (row: SignedDocumentDTO) =>
                row.visibleToClient !== null && row.visibleToClient !== undefined ? (
                  <ToggleSwitch
                    checked={!!row.visibleToClient}
                    disabled={togglingId === row.id}
                    onChange={(next) => { void handleToggleVisibility(row, next); }}
                    aria-label={`Show ${row.documentName || 'document'} to client`}
                  />
                ) : (
                  <span className="loa-count">—</span>
                ),
            },
          ]
        : []),
    ],
    [relationshipMode, togglingId, handleToggleVisibility]
  );

  const actions = useMemo<LOATableAction<SignedDocumentDTO>[]>(
    () => [
      {
        key: 'view',
        icon: <EyeIcon />,
        label: 'View details',
        onClick: (row) => { void handleViewPdf(row); },
        visible: (row) => hasViewableDocument(row),
      },
      {
        key: 'download',
        icon: <DownloadIcon />,
        label: 'Download signed document',
        onClick: (row) => { void handleDownload(row); },
        visible: (row) => hasViewableDocument(row),
        disabled: (row) => downloadingId === row.id,
        loading: (row) => downloadingId === row.id,
      },
      {
        key: 'delete',
        icon: <TrashIcon />,
        label: 'Delete document',
        onClick: (row) => setDeletingRow(row),
        // Relationship tab only. Soft-delete signed LoA/CoA and advisor uploads;
        // client uploads aren't deletable from here (the client owns them) and
        // sealed assessment records are never deletable (regulatory archive).
        visible: (row) =>
          relationshipMode
          && !isReadOnlyArtifact(row.documentType)
          && (row.documentType !== 'UPLOAD' || row.uploadedByRole === 'ADVISOR'),
      },
    ],
    [downloadingId, handleViewPdf, handleDownload, relationshipMode]
  );

  const uploadHeaderAction = relationshipMode ? (
    <Button
      variant="filled"
      size="lg"
      onClick={() => setUploadOpen(true)}
      data-testid="upload-relationship-document-button"
    >
      Upload Document
    </Button>
  ) : undefined;

  return (
    <>
      <LOATableCard
        data={page.content}
        columns={columns}
        actions={readOnly ? undefined : actions}
        keyExtractor={(row) => `${row.documentType}-${row.id}`}
        loading={loading}
        error={error}
        onRetry={() => loadPage(currentPage - 1)}
        title="Documents"
        headerAction={uploadHeaderAction}
        emptyMessage="No client signed documents to show"
        aboveTable={aboveTable}
        pagination={{
          currentPage,
          totalPages: page.totalPages,
          itemsPerPage: pageSize,
          onPageChange: setCurrentPage,
        }}
      />

      <ViewLoAModal
        open={!!viewPdfState}
        onClose={handleClosePdfViewer}
        documentUrl={pdfUrl}
        title={viewPdfState?.title || ''}
        loading={pdfLoading}
        contentType={viewContentType}
      />

      {relationshipMode && (
        <UploadRelationshipDocumentModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onUpload={handleUpload}
        />
      )}

      {deletingRow && (
        <DeleteDocumentModal
          open
          documentId={deletingRow.id}
          documentName={deletingRow.documentName ?? undefined}
          documentType={deletingRow.documentType}
          onClose={() => setDeletingRow(null)}
          onSuccess={() => { setDeletingRow(null); void loadPage(currentPage - 1); }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------- //
// Status label / badge helpers — shared between LOA and CoA statuses
// ---------------------------------------------------------------------- //

const SIGNED_STATUS_LABELS: Record<SignedDocumentStatus, string> = {
  DRAFT: 'Draft',
  GENERATING: 'Generating',
  PENDING_SIGNATURE: 'Pending Signature',
  PARTIALLY_SIGNED: 'Partially Signed',
  SIGNED: 'Signed',
  PENDING_PROVIDER: 'Pending Provider',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  COMPLETED: 'Completed',
  DECLINED: 'Declined',
  FAILED: 'Failed',
  EXPIRED: 'Expired',
  VOIDED: 'Voided',
};

function getStatusLabel(status: SignedDocumentStatus | null): string {
  if (!status) return '—';
  return SIGNED_STATUS_LABELS[status] ?? status;
}

function getStatusBadgeClass(status: SignedDocumentStatus | null): string {
  const statusLower = String(status).toLowerCase().replaceAll('_', '-');
  return `loa-status-badge loa-status-badge--${statusLower}`;
}

/**
 * Read-only artifact rows — a stored PDF that's never edited/deleted from this
 * tab (sealed assessments, proposal Statements of Suitability, New Business
 * application forms). They always carry a file, and are never soft-deletable.
 */
function isReadOnlyArtifact(documentType: SignedDocumentType): boolean {
  return (
    documentType === 'ASSESSMENT' ||
    documentType === 'SOS' ||
    documentType === 'NEW_BUSINESS'
  );
}

/**
 * Rows with an always-available artifact: uploads and read-only artifacts
 * (assessments / SoS / New Business forms) carry their stored file directly;
 * LoA/CoA rows only have a signed PDF once the workflow reaches a terminal
 * signed state.
 */
function hasViewableDocument(row: SignedDocumentDTO): boolean {
  return (
    row.documentType === 'UPLOAD' ||
    isReadOnlyArtifact(row.documentType) ||
    isTerminalSignedStatus(row.status)
  );
}

/**
 * Returns true when a signed-document row is in a state that has an
 * actionable signed artifact (i.e. the PDF can be viewed / downloaded).
 * Mirrors the old LOAList precedent which only exposed view+download
 * for COMPLETED LOAs, and extends the same rule to the CoA-specific
 * terminal states that also produce a signed PDF.
 */
function isTerminalSignedStatus(status: SignedDocumentStatus | null): boolean {
  if (!status) return false;
  return (
    status === 'COMPLETED' ||
    status === 'SIGNED' ||
    status === 'PENDING_PROVIDER' ||
    status === 'ACCEPTED' ||
    status === 'REJECTED'
  );
}

// Re-export document type for consumers who want to narrow on it
export type { SignedDocumentType } from '../types/signedDocument';
